/**
 * Webview Detection Utility
 *
 * Detects whether the application is running in a native mobile webview
 * (iOS WKWebView/UIWebView, Android WebView) or standalone browser.
 *
 * This detection is crucial for determining how to handle certain features:
 * - postMessage communication with native app
 * - Location permission handling
 * - Deep linking behavior
 */

const NATIVE_APP_FLAG = "isNativeApp";
const WEBVIEW_INDICATORS = {
  iOS: [
    /webkit\/(?:.*)\s(?:UIWebView|UIWebViewBuildNumber)/i,
    /Mobile\/\w+\s(?:Safari)/i
  ],
  Android: [/wv\s/, /WebView\//, /\bAndroid\b.*\bwv\b/]
};

/**
 * Check if running in native app environment
 * Uses the existing window.isNativeApp flag if available
 *
 * @returns {boolean}
 */
export const isNativeApp = () => {
  if (typeof window === "undefined") {
    return false;
  }

  if (window[NATIVE_APP_FLAG] === true) {
    return true;
  }

  return false;
};

/**
 * Detect iOS webview
 *
 * @returns {boolean}
 */
const isIOSWebview = () => {
  if (typeof window === "undefined" || !navigator) {
    return false;
  }

  const userAgent = navigator.userAgent || "";

  // Check for webkit UIWebView indicators
  return WEBVIEW_INDICATORS.iOS.some(pattern => pattern.test(userAgent));
};

/**
 * Detect Android webview
 *
 * @returns {boolean}
 */
const isAndroidWebview = () => {
  if (typeof window === "undefined" || !navigator) {
    return false;
  }

  const userAgent = navigator.userAgent || "";

  // Check for WebView indicators
  return WEBVIEW_INDICATORS.Android.some(pattern => pattern.test(userAgent));
};

/**
 * Main webview detection function
 * Checks multiple indicators to determine if running in webview
 *
 * @returns {boolean} - True if running in any webview environment
 */
export const isWebview = () => {
  // First check the explicit flag (most reliable)
  if (isNativeApp()) {
    return true;
  }

  // Check for iOS webview
  if (isIOSWebview()) {
    return true;
  }

  // Check for Android webview
  if (isAndroidWebview()) {
    return true;
  }

  return false;
};

/**
 * Get detailed environment information
 *
 * @returns {Object} - Environment details
 */
export const getEnvironmentInfo = () => {
  if (typeof window === "undefined") {
    return {
      isBrowser: false,
      isWebview: false,
      isNativeApp: false,
      platform: "server"
    };
  }

  const userAgent = navigator.userAgent || "";
  const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(userAgent);

  return {
    isBrowser: true,
    isWebview: isWebview(),
    isNativeApp: isNativeApp(),
    isMobile,
    platform: isMobile ? "mobile" : "desktop",
    userAgent
  };
};

/**
 * Wait for native app to be ready
 * Useful when native app needs to set window.isNativeApp after load
 *
 * @param {number} timeout - Maximum time to wait in ms
 * @returns {Promise<boolean>}
 */
export const waitForNativeApp = (timeout = 5000) => {
  return new Promise(resolve => {
    if (isNativeApp()) {
      resolve(true);
      return;
    }

    const checkInterval = 100;
    let elapsed = 0;

    const check = () => {
      elapsed += checkInterval;

      if (isNativeApp()) {
        resolve(true);
        return;
      }

      if (elapsed >= timeout) {
        resolve(false);
        return;
      }

      setTimeout(check, checkInterval);
    };

    check();
  });
};

export default {
  isWebview,
  isNativeApp,
  getEnvironmentInfo,
  waitForNativeApp
};
